import React, { useState, useEffect } from 'react';
import { useTranslate, useNotify, useRedirect } from 'react-admin';
import {
  Divider,
  Link,
  Button,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
import capitalizeFirstLetter from '../../utils/helpers/capitalizeFirstLetter';
import { updateResource } from '../../utils/requests';

import './styles.scss';

interface OnboardingCheckInFormProps {
  readonly: boolean;
  data: any;
  stepId: string;
}

const OnboardingCheckInForm = ({
  readonly,
  data,
  stepId,
}: OnboardingCheckInFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const stepData = data.steps.find((step: any) => step.id === stepId);
  const stepWithPrescription = data.steps.find((step: any) => {
    const stepIdWithPrescription =
      stepId === 'checkInMedicationOne'
        ? 'bookFirstTimeIntakeMedicationOne'
        : 'bookFirstTimeIntakeMedicationTwo';
    return step.id === stepIdWithPrescription;
  });

  const title = translate('immunotherapy.onboardingCheckIn.title');
  const medicationName = capitalizeFirstLetter(
    stepWithPrescription?.medication,
  );
  const [canPatientProceed, setCanPatientProceed] = useState<string>(
    stepData?.consultationData?.canPatientProceed
      ? stepData?.consultationData?.canPatientProceed
      : '',
  );

  // State for form submission
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // State for accordion expand/collapse
  const [isExpanded, setIsExpanded] = useState<boolean>(!readonly);

  // Clear errors whenever form state changes
  useEffect(() => {
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  }, [canPatientProceed]);

  const handleCanPatientProceedChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setCanPatientProceed(event.target.value);
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    // Validate canPatientProceed is selected
    if (!canPatientProceed) {
      errors.push(
        `${translate(
          'immunotherapy.onboardingCheckIn.canPatientProceed',
        )} ${translate('ra.validation.required')}`,
      );
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (errors.length > 0) {
      return;
    }

    try {
      setIsLoading(true);

      const updates = {
        activeStepId: stepData?.id,
        canPatientProceed: canPatientProceed === 'true',
      };

      await updateResource(`treatments/${data.id}`, updates);
      notify('tests.addReportSuccessMessage', { type: 'success' });
      redirect('/treatments');
    } catch (e) {
      notify('tests.addReportErrorMessage', { type: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const prescriptionUrl =
    stepWithPrescription?.consultationData?.uploadedFile?.url;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div className="onboardingCheckIn-form">
      <Divider className="divider" />
      <div className="formWrapper">
        <div
          className="accordion-header"
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <SectionTitle title={`${title}: ${medicationName}`} />
          <IconButton
            className="accordion-toggle-icon"
            size="small"
            aria-label="expand or collapse"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        {isExpanded && (
          <>
            <div className="treatmentBlock onboardingCheckIn-treatmentBlock">
              <div className="treatmentBlock-subtitle">
                {translate(
                  'immunotherapy.onboardingCheckIn.medicationForReview',
                )}
              </div>
              <div className="treatmentBlock-text">{medicationName}</div>
            </div>
            <div className="treatmentBlock onboardingCheckIn-treatmentBlock">
              <div className="firstTimeIntake-question-container">
                <div className="treatmentBlock-subtitle">
                  {translate(
                    'immunotherapy.onboardingCheckIn.canPatientProceed',
                  )}
                </div>
              </div>
              <CustomSelect
                emptyLabel={translate(
                  'immunotherapy.treatment.selectEligibility',
                )}
                options={[
                  {
                    value: 'true',
                    label: translate('immunotherapy.treatment.yes'),
                  },
                  {
                    value: 'false',
                    label: translate('immunotherapy.treatment.no'),
                  },
                ]}
                value={canPatientProceed}
                onChange={handleCanPatientProceedChange}
                disabled={readonly}
                error={!canPatientProceed}
              />
            </div>
            {prescriptionUrl && (
              <div className="treatmentBlock onboardingCheckIn-treatmentBlock">
                <Link
                  href={prescriptionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text--purple"
                  style={{ fontSize: '16px', textDecoration: 'underline' }}
                >
                  {translate('immunotherapy.treatment.openPrescription')}
                </Link>
              </div>
            )}
            {formErrors.length > 0 && (
              <div className="formErrors">
                <ul>
                  {formErrors.map((error) => (
                    <li key={error} className="error">
                      * {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!readonly && (
              <div className="treatmentBlock">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24.5} />
                  ) : (
                    translate(
                      'immunotherapy.onboardingCheckIn.submitButtonText',
                    )
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingCheckInForm;
